import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Field, Form } from "react-final-form";
import { Modal, TextInput, ContainedButton } from "shared/components";
import { SubscriptionReportModalProps } from "./SubscriptionReportModal.props";
import { reportCommentValidator } from "./SubscriptionReportModal.validation";
import { Title, Wrapper, TableWrapper, SubmitButtonWrapper } from "../ReportModalStyles";
import { ReportTable } from "../ReportTable";
import { DataTableColumn } from "../DataTable";
import { useSubscriptionsReport } from "../../hooks";

const columns: DataTableColumn[] = [
  {
    name: "timeOfSubscription",
    translationKey: "dashboardModule.subscriptionTable.table.columns.timeOfSubscription",
    minWidth: 200
  },
  {
    name: "templateType",
    translationKey: "dashboardModule.subscriptionTable.table.columns.templateType",
    minWidth: 180
  },
  {
    name: "language",
    translationKey: "dashboardModule.subscriptionTable.table.columns.language",
    minWidth: 100
  },
  {
    name: "email",
    translationKey: "dashboardModule.subscriptionTable.table.columns.email",
    minWidth: 200
  },
  {
    name: "firstName",
    translationKey: "dashboardModule.subscriptionTable.table.columns.firstName",
    minWidth: 150
  },
  {
    name: "lastName",
    translationKey: "dashboardModule.subscriptionTable.table.columns.lastName",
    minWidth: 150
  },
  {
    name: "gender",
    translationKey: "dashboardModule.subscriptionTable.table.columns.gender",
    minWidth: 100
  },
  {
    name: "birthdate",
    translationKey: "dashboardModule.subscriptionTable.table.columns.birthdate",
    minWidth: 150
  }
];

export const SubscriptionReportModal = ({
  open,
  onClose,
  onSubmit,
  data,
  dataQueryKey
}: SubscriptionReportModalProps) => {
  const { t } = useTranslation();

  const subscriptionIds = useMemo(() => data.map(({ id }) => id), [data]);

  const reportSubscriptions = useSubscriptionsReport(dataQueryKey, subscriptionIds, onSubmit);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("dashboardModule.subscriptionTable.reportModal.title")}
      variant="bright"
    >
      <Wrapper>
        <TableWrapper>
          <Title>{t("dashboardModule.subscriptionTable.reportModal.description")}</Title>
          <ReportTable data={data} columns={columns} />
        </TableWrapper>
        <Form
          onSubmit={reportSubscriptions}
          render={({ handleSubmit, submitErrors, dirty, submitting }) => (
            <>
              <Field
                name="reportComment"
                validate={reportCommentValidator}
                render={({
                  input: { onBlur, onChange, onFocus, value },
                  meta: { error, touched, modifiedSinceLastSubmit }
                }) => (
                  <TextInput
                    label={t("dashboardModule.subscriptionTable.reportModal.form.reason.label")}
                    placeholder={t(
                      "dashboardModule.subscriptionTable.reportModal.form.reason.placeholder"
                    )}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    fullWidth
                    multiline
                    marginTop={36}
                    error={
                      (error || (submitErrors?.["reportComment"] && !modifiedSinceLastSubmit)) &&
                      touched
                    }
                    errorMessage={t(submitErrors?.["reportComment"] ?? error)}
                  />
                )}
              />
              <SubmitButtonWrapper>
                <ContainedButton
                  onClick={handleSubmit}
                  fullWidth
                  disabled={!dirty}
                  loading={submitting}
                >
                  {t("dashboardModule.subscriptionTable.reportModal.form.submitButton")}
                </ContainedButton>
              </SubmitButtonWrapper>
            </>
          )}
        />
      </Wrapper>
    </Modal>
  );
};
